import React, { useState } from 'react';
import { Box, Button, FormControlLabel, Switch, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { integrationApiService } from '../../services/integrationApi';
import type {
  IntegrationConfig,
  IntegrationSettings,
  OverrideFieldSettings,
  RebateDiscountSettings,
} from '../../services/integrationApi';
import OverrideFieldsPicker from '../OverrideFieldsPicker';
import { InlineMessage, SectionCard } from '../ui';
import { rebateDiscountOn } from '../integrationSetup/rebateDiscountSync';

const T = 'integrations.lightspeedConfig';

const formatWhen = (value: unknown): string => {
  if (!value) return '';
  const record = value as Record<string, any>;
  const seconds = typeof record.seconds === 'number' ? record.seconds : typeof record._seconds === 'number' ? record._seconds : null;
  const date = seconds != null ? new Date(seconds * 1000) : new Date(String(value));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

export interface LightspeedKeepFieldsSectionProps {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
}

/** Which item fields Motzu may rewrite on items already in Lightspeed (flag `lightspeed_store_config`). */
export const LightspeedKeepFieldsSection: React.FC<LightspeedKeepFieldsSectionProps> = ({ settings, onChange }) => {
  const { t } = useAppTranslation();
  if (settings.syncMode === 'link_only') return null;
  const field = (key: 'title' | 'description' | 'images') => ({
    label: t(`${T}.keep.${key}.label`),
    desc: t(`${T}.keep.${key}.desc`),
  });
  return (
    <SectionCard title={t(`${T}.keep.section`)} dense>
      <OverrideFieldsPicker
        value={settings.overrideFields}
        onChange={(overrideFields: OverrideFieldSettings) => onChange({ overrideFields })}
        imagesSyncEnabled={settings.syncImages !== false}
        fields={['title', 'description', 'images']}
        copy={{
          title: t(`${T}.keep.heading`),
          subtitle: t(`${T}.keep.subtitle`),
          keepNote: t(`${T}.keep.note`),
          fields: { title: field('title'), description: field('description'), images: field('images') },
        }}
      />
    </SectionCard>
  );
};

/** The store's switch for register Discounts on one Lightspeed integration (missing = on). */
export const LightspeedDiscountChoice: React.FC<{ checked: boolean; onChange: (checked: boolean) => void }> = ({
  checked,
  onChange,
}) => {
  const { t } = useAppTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <FormControlLabel
        control={<Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />}
        label={
          <Box>
            <Typography variant="body2">{t(`${T}.discounts.switch`)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {checked ? t(`${T}.discounts.on`) : t(`${T}.discounts.off`)}
            </Typography>
          </Box>
        }
      />
      <Typography variant="caption" color="text.secondary">
        {t(`${T}.discounts.treatmentNote`)}
      </Typography>
    </Box>
  );
};

export interface LightspeedRebateDiscountsSectionProps {
  integration: IntegrationConfig;
  rebateDiscounts?: RebateDiscountSettings;
  onRebateDiscountsChange: (next: RebateDiscountSettings) => void;
}

/**
 * Register Discounts Motzu keeps in Lightspeed for live instant rebates (flags
 * `lightspeed_sales_sync` and `lightspeed_store_config`): the store's switch, the last result
 * and Sync now.
 */
export const LightspeedRebateDiscountsSection: React.FC<LightspeedRebateDiscountsSectionProps> = ({
  integration,
  rebateDiscounts,
  onRebateDiscountsChange,
}) => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const last = integration.lightspeedDiscountSync;
  const on = rebateDiscountOn(rebateDiscounts, 'instant');

  const sync = useMutation({
    mutationFn: () => integrationApiService.syncLightspeedRebateDiscounts(integration.id),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['integration', integration.id] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (err: Error) => setError(err.message || t(`${T}.discounts.failed`)),
  });

  return (
    <SectionCard title={t(`${T}.discounts.title`)} subtitle={t(`${T}.discounts.subtitle`)} dense>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <LightspeedDiscountChoice
          checked={on}
          onChange={(instant) => onRebateDiscountsChange({ ...rebateDiscounts, instant })}
        />
        {integration.salesSyncAuthRequired && (
          <InlineMessage severity="warning">{t(`${T}.discounts.reconnect`)}</InlineMessage>
        )}
        {(last?.errorCount || 0) > 0 && (
          <InlineMessage severity="error">{t(`${T}.discounts.errors`, { count: last!.errorCount })}</InlineMessage>
        )}
        {error && <InlineMessage severity="error">{error}</InlineMessage>}
        <Typography variant="body2" color="text.secondary">
          {last
            ? t(`${T}.discounts.last`, {
                when: formatWhen(last.at),
                created: last.created,
                updated: last.updated,
                archived: last.archived,
                skipped: last.skipped,
              })
            : t(`${T}.discounts.never`)}
        </Typography>
        <Box>
          <Button size="small" variant="outlined" onClick={() => sync.mutate()} disabled={sync.isPending}>
            {sync.isPending ? t(`${T}.discounts.syncing`) : t(`${T}.discounts.syncNow`)}
          </Button>
        </Box>
      </Box>
    </SectionCard>
  );
};
